(() => {
  const COMPONENT_NAME = "ugc-videowise-carousel";

  if (customElements.get(COMPONENT_NAME)) return;

  const NATIVE_PREV = ".vw-previous-slide-button";
  const NATIVE_NEXT = ".vw-next-slide-button";
  const LINKED_PRODUCTS_STYLE_ID = "ugc-linked-products-style";
  const HIDE_NATIVE_ARROWS_STYLE_ID = "ugc-hide-native-arrows-style";
  const LAYOUT_STYLE_ID = "ugc-layout-style";
  const WAIT_MS = 250;
  const SLOW_WAIT_MS = 1000;
  const FAST_WAIT_ATTEMPTS = 80;
  const SLIDE_GAP = 16;
  const SLIDE_FLEX_BASIS = 300;
  const PLAY_BUTTON_CLASS = "ugc-vw-play";
  const PLAY_ICON_SVG = `
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path d="M16 0C7.17474 0 0 7.17474 0 16C0 24.8253 7.17474 32 16 32C24.8253 32 32 24.8253 32 16C32 7.17474 24.8253 0 16 0ZM21.1537 16.8084L13.6084 21.8274C12.9684 22.2653 12.0926 21.7937 12.0926 21.0189V10.9811C12.0926 10.2063 12.9684 9.73474 13.6084 10.1726L21.1537 15.1916C21.7263 15.5621 21.7263 16.4379 21.1537 16.8084Z" fill="white" fill-opacity="0.8"/>
    </svg>
  `.trim();
  const HIDE_LINKED_PRODUCTS_CSS = `
    .vw--inline-shoppable-product {
      display: none !important;
    }
  `;
  const HIDE_NATIVE_ARROWS_CSS = `
    .vw-previous-slide-button,
    .vw-next-slide-button {
      display: none !important;
      pointer-events: none !important;
    }
  `;
  const LAYOUT_CSS = `
    #vw-root [class^="rw__inline-widget--container-"] > div:first-child {
      height: auto !important;
      margin-inline: 0 !important;
      min-height: 0 !important;
      padding-inline: 0 !important;
      width: 100% !important;
    }
  `;

  /**
   * Videowise reserves a viewport far taller than its mobile cards need, so the
   * widget trails a block of empty space. The cards keep their aspect ratio —
   * only the reserved space is capped, from the merchant's mobile height.
   */
  const mobileHeightCss = (height) => `
    @media screen and (max-width: 767px) {
      #vw-root,
      #vw-root [class^="rw__inline-widget--container-"],
      #vw-root [class^="rw__inline-widget--container-"] > div:first-child {
        max-height: ${height}px !important;
        overflow: hidden !important;
      }

      #vw-root [role="group"] {
        max-height: ${height}px !important;
      }
    }
  `;

  class UgcVideowiseCarousel extends HTMLElement {
    #abortController = null;
    #prevButton = null;
    #nextButton = null;
    #progress = null;
    #widget = null;
    #shadowRoot = null;
    #nativePrev = null;
    #nativeNext = null;
    #waitAttempts = 0;
    #waitTimer = null;
    #slideCount = 0;
    #index = 0;
    #trackObserver = null;
    #trackFindObserver = null;
    #observedTrack = null;
    #syncTimer = null;
    #navBusy = false;
    #playObserver = null;

    static get observedAttributes() {
      return ["data-show-linked-products", "data-embed-height-mobile"];
    }

    connectedCallback() {
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.#prevButton = this.querySelector("[data-ugc-prev]");
      this.#nextButton = this.querySelector("[data-ugc-next]");
      this.#progress = this.querySelector("[data-ugc-progress]");
      this.#widget = this.querySelector(".reeview-app-widget");

      this.#prevButton?.addEventListener("click", this.#onPrev, { signal });
      this.#nextButton?.addEventListener("click", this.#onNext, { signal });

      this.#waitForNativeControls();
    }

    attributeChangedCallback(name) {
      if (!this.#shadowRoot) return;

      if (name === "data-show-linked-products") {
        this.#applyLinkedProductsPreference();
        return;
      }

      if (name === "data-embed-height-mobile") {
        this.#applyLayoutStyle();
      }
    }

    disconnectedCallback() {
      this.#clearWait();
      this.#clearSyncTimer();
      this.#trackObserver?.disconnect();
      this.#trackObserver = null;
      this.#trackFindObserver?.disconnect();
      this.#trackFindObserver = null;
      this.#observedTrack = null;
      this.#playObserver?.disconnect();
      this.#playObserver = null;
      this.#abortController?.abort();
      this.#abortController = null;
      this.#navBusy = false;
      this.#removeLinkedProductsStyle();
      this.#removeNativeArrowsStyle();
      this.#removeLayoutStyle();
      this.#shadowRoot = null;
      this.#nativePrev = null;
      this.#nativeNext = null;
      this.classList.remove("ugc-videowise-carousel--ready");
    }

    #onPrev = () => {
      this.#advance(-1);
    };

    #onNext = () => {
      this.#advance(1);
    };

    /**
     * Videowise’s first next/prev from a settled position often only
     * “snaps” ~10–20px. Keep clicking until we’ve moved at least half a
     * card so each theme arrow press advances a full slide.
     * Matches native Videowise looping — do not block at the ends.
     */
    #advance(direction) {
      if (this.#navBusy) return;

      const viewCount = this.#getViewCount();

      if (viewCount <= 1) return;

      const button = direction < 0 ? this.#nativePrev : this.#nativeNext;

      if (!button) return;

      const track = this.#getTrack();
      const start = this.#readTrackX(track);
      const startIndex = this.#index;

      if (start === null) {
        button.click();
        this.#scheduleSync();
        return;
      }

      this.#navBusy = true;

      const minDelta = this.#getMotionStep() * 0.45;
      let attempts = 0;

      const tick = () => {
        attempts += 1;
        button.click();

        window.setTimeout(() => {
          if (!this.isConnected) {
            this.#navBusy = false;
            return;
          }

          this.#syncFromTrack();

          const now = this.#readTrackX(this.#getTrack());
          const moved = now === null ? minDelta : Math.abs(start - now);
          const indexChanged = this.#index !== startIndex;

          if (indexChanged || moved >= minDelta || attempts >= 3) {
            this.#navBusy = false;
            this.#syncFromTrack();
            return;
          }

          tick();
        }, 280);
      };

      tick();
    }

    #waitForNativeControls() {
      if (!this.isConnected) return;

      const root = this.#resolveShadowRoot();

      if (root) {
        const prev = root.querySelector(NATIVE_PREV);
        const next = root.querySelector(NATIVE_NEXT);

        if (prev && next) {
          this.#clearWait();
          this.#shadowRoot = root;
          this.#nativePrev = prev;
          this.#nativeNext = next;
          this.#applyLinkedProductsPreference();
          this.#hideNativeArrows();
          this.#applyLayoutStyle();
          this.#alignSlideFlexBasis();
          this.#refreshSlideCount();
          this.#mountPlayOverlays();
          this.#observeSlideDom();
          this.#observeTrack();
          this.classList.add("ugc-videowise-carousel--ready");
          this.#syncFromTrack();
          return;
        }
      }

      this.#waitAttempts += 1;

      /*
       * Videowise exposes closed shadow roots late (often after lazy init).
       * Keep polling while connected — a hard timeout left arrows permanently
       * disabled once the widget finally appeared.
       */
      const delay =
        this.#waitAttempts > FAST_WAIT_ATTEMPTS ? SLOW_WAIT_MS : WAIT_MS;

      this.#waitTimer = window.setTimeout(
        () => this.#waitForNativeControls(),
        delay,
      );
    }

    #clearWait() {
      if (this.#waitTimer === null) return;

      window.clearTimeout(this.#waitTimer);
      this.#waitTimer = null;
    }

    #clearSyncTimer() {
      if (this.#syncTimer === null) return;

      window.clearTimeout(this.#syncTimer);
      this.#syncTimer = null;
    }

    #scheduleSync() {
      this.#clearSyncTimer();
      this.#syncTimer = window.setTimeout(() => {
        this.#syncFromTrack();
      }, 400);
    }

    #resolveShadowRoot() {
      this.#widget = this.querySelector(".reeview-app-widget");

      const roots = window.videowiseShadowRoots;

      if (!roots || typeof roots !== "object") return null;

      const widget = this.#widget;

      if (!widget) return null;

      const rawId = widget.id || "";
      const shortId = rawId.replace(/^reeview-app-widget_/, "");
      const candidateKeys = new Set(
        [rawId, shortId, shortId ? `reeview-app-widget_${shortId}` : ""].filter(
          Boolean,
        ),
      );

      for (const key of candidateKeys) {
        if (roots[key] instanceof ShadowRoot) {
          return roots[key];
        }
      }

      // Match by host belonging to this carousel — never fall back to another
      // widget’s root when IDs don’t line up.
      for (const value of Object.values(roots)) {
        if (!(value instanceof ShadowRoot)) continue;

        const host = value.host;

        if (!(host instanceof Element)) continue;

        if (host === widget || widget.contains(host) || this.contains(host)) {
          return value;
        }
      }

      return null;
    }

    /**
     * Videowise stores and steps by flex: 0 0 300px. Keep the inline
     * basis in sync so native next/prev advance one card at a time.
     */
    #alignSlideFlexBasis() {
      if (!this.#shadowRoot) return;

      for (const slide of this.#shadowRoot.querySelectorAll('[role="group"]')) {
        slide.style.flex = `0 0 ${SLIDE_FLEX_BASIS}px`;
      }
    }

    /**
     * Play mark — centered over each thumb, hidden while that video plays.
     */
    #mountPlayOverlays() {
      if (!this.#shadowRoot || !this.#abortController) return;

      const { signal } = this.#abortController;

      for (const button of this.#shadowRoot.querySelectorAll(
        '[role="group"] > [role="button"]',
      )) {
        if (button.querySelector(`.${PLAY_BUTTON_CLASS}`)) continue;

        const video = button.querySelector("video");

        if (!video) continue;

        const play = document.createElement("span");

        play.className = PLAY_BUTTON_CLASS;
        play.setAttribute("aria-hidden", "true");
        play.innerHTML = PLAY_ICON_SVG;
        button.append(play);

        const isPlayable = () => {
          const src = video.currentSrc || video.getAttribute("src") || "";

          // Still images / broken assets render a <video> with empty src + poster
          return Boolean(src) && !src.endsWith("/") && !video.error;
        };

        const syncVisibility = () => {
          play.hidden = !isPlayable() || !video.paused;
        };

        video.addEventListener("play", syncVisibility, { signal });
        video.addEventListener("playing", syncVisibility, { signal });
        video.addEventListener("pause", syncVisibility, { signal });
        video.addEventListener("ended", syncVisibility, { signal });
        video.addEventListener("error", syncVisibility, { signal });
        video.addEventListener("loadedmetadata", syncVisibility, { signal });
        video.addEventListener("emptied", syncVisibility, { signal });
        syncVisibility();
      }
    }

    /**
     * Videowise can inject/replace slides after the first paint. Keep flex
     * basis, play marks, view count, and progress pills in sync — not just
     * overlays.
     */
    #observeSlideDom() {
      if (!this.#shadowRoot) return;

      this.#playObserver?.disconnect();

      const host =
        this.#shadowRoot.querySelector("#vw-root") || this.#shadowRoot;

      this.#playObserver = new MutationObserver(() => {
        this.#onSlideDomMutated();
      });

      this.#playObserver.observe(host, {
        childList: true,
        subtree: true,
      });
    }

    #onSlideDomMutated() {
      this.#alignSlideFlexBasis();
      this.#mountPlayOverlays();
      this.#refreshSlideCount();
      this.#scheduleSync();
    }

    #refreshSlideCount() {
      if (!this.#shadowRoot) return;

      this.#slideCount =
        this.#shadowRoot.querySelectorAll('[role="group"]').length;
    }

    /**
     * Videowise moves the track in fixed flex-basis (+ gap) increments —
     * not by the card’s rendered width (which may be clamp()ed below 300px).
     */
    #getMotionStep() {
      return SLIDE_FLEX_BASIS + SLIDE_GAP;
    }

    /**
     * One progress segment per slide item (matches each Videowise card).
     */
    #getViewCount() {
      this.#refreshSlideCount();

      return Math.max(1, this.#slideCount);
    }

    #getTrack() {
      if (!this.#shadowRoot) return null;

      // Prefer the real slide track (avoids modal/keen translate3d nodes).
      const slide = this.#shadowRoot.querySelector("#slide-0");

      if (slide?.parentElement) return slide.parentElement;

      return [...this.#shadowRoot.querySelectorAll("div")].find((node) =>
        (node.getAttribute("style") || "").includes("translate3d"),
      );
    }

    /**
     * Attach to the translate3d track when ready. Videowise often mounts
     * arrows before the track gets an inline transform — keep discovering
     * until it appears so swipe/in-widget nav can update progress pills.
     */
    #observeTrack() {
      if (!this.#shadowRoot) return;

      const track = this.#getTrack();

      if (!track) {
        this.#startTrackDiscovery();
        return;
      }

      this.#stopTrackDiscovery();

      if (this.#observedTrack === track) return;

      this.#trackObserver?.disconnect();
      this.#observedTrack = track;

      this.#trackObserver = new MutationObserver(() => {
        this.#scheduleSync();
      });

      this.#trackObserver.observe(track, {
        attributes: true,
        attributeFilter: ["style"],
      });
    }

    #startTrackDiscovery() {
      if (!this.#shadowRoot || this.#trackFindObserver) return;

      const host =
        this.#shadowRoot.querySelector("#vw-root") || this.#shadowRoot;

      this.#trackFindObserver = new MutationObserver(() => {
        if (!this.#getTrack()) return;

        this.#observeTrack();
        this.#syncFromTrack();
      });

      this.#trackFindObserver.observe(host, {
        attributes: true,
        attributeFilter: ["style"],
        childList: true,
        subtree: true,
      });
    }

    #stopTrackDiscovery() {
      this.#trackFindObserver?.disconnect();
      this.#trackFindObserver = null;
    }

    #readTrackX(track) {
      const match = track?.style?.transform?.match(/translate3d\(([-\d.]+)px/);

      if (!match) return null;

      return Number(match[1]);
    }

    /**
     * Active progress index = leading slide in the viewport (slide-0 … slide-n).
     * Scoped to the main carousel track (same as #getTrack) so modal/lightbox
     * clones cannot win the nearest-edge score.
     */
    #getActiveSlideIndex() {
      if (!this.#shadowRoot || !this.#widget) return 0;

      const track = this.#getTrack();

      if (!track) return 0;

      const slides = [...track.querySelectorAll('[id^="slide-"]')];

      if (slides.length === 0) return 0;

      const viewportLeft = this.#widget.getBoundingClientRect().left;
      let bestIndex = 0;
      let bestScore = Infinity;

      for (const slide of slides) {
        const idMatch = slide.id.match(/^slide-(\d+)$/);

        if (!idMatch) continue;

        const index = Number(idMatch[1]);
        const slideLeft = slide.getBoundingClientRect().left;
        // Prefer the slide whose left edge is nearest the widget’s left edge.
        const score = Math.abs(slideLeft - viewportLeft);

        if (score < bestScore) {
          bestScore = score;
          bestIndex = index;
        }
      }

      return bestIndex;
    }

    #syncFromTrack() {
      // Re-attempt attach if the track only appeared after arrows/sync.
      this.#observeTrack();

      const viewCount = this.#getViewCount();

      this.#index = Math.max(
        0,
        Math.min(this.#getActiveSlideIndex(), viewCount - 1),
      );
      this.#renderProgress();
      this.#syncNavButtons(viewCount);
    }

    /**
     * Keep theme arrows available while the widget can loop (native Videowise).
     * Only disable before ready, or when there is nothing to scroll.
     */
    #syncNavButtons(viewCount = this.#getViewCount()) {
      const ready = Boolean(this.#nativePrev && this.#nativeNext);
      const disabled = !ready || viewCount <= 1;

      if (this.#prevButton) this.#prevButton.disabled = disabled;
      if (this.#nextButton) this.#nextButton.disabled = disabled;
    }

    #showLinkedProducts() {
      return this.getAttribute("data-show-linked-products") === "true";
    }

    #applyLinkedProductsPreference() {
      if (!this.#shadowRoot) return;

      if (this.#showLinkedProducts()) {
        this.#removeLinkedProductsStyle();
        return;
      }

      let style = this.#shadowRoot.getElementById(LINKED_PRODUCTS_STYLE_ID);

      if (!style) {
        style = document.createElement("style");
        style.id = LINKED_PRODUCTS_STYLE_ID;
        this.#shadowRoot.append(style);
      }

      style.textContent = HIDE_LINKED_PRODUCTS_CSS;
    }

    #removeLinkedProductsStyle() {
      this.#shadowRoot?.getElementById(LINKED_PRODUCTS_STYLE_ID)?.remove();
    }

    /**
     * Hide Videowise’s own prev/next only in this UGC shadow root — the theme
     * footer arrows drive navigation. Global custom-videowise.css must not hide
     * arrows so PDP product-social-gallery keeps native controls.
     */
    #hideNativeArrows() {
      if (!this.#shadowRoot) return;

      let style = this.#shadowRoot.getElementById(HIDE_NATIVE_ARROWS_STYLE_ID);

      if (!style) {
        style = document.createElement("style");
        style.id = HIDE_NATIVE_ARROWS_STYLE_ID;
        this.#shadowRoot.append(style);
      }

      style.textContent = HIDE_NATIVE_ARROWS_CSS;
    }

    #removeNativeArrowsStyle() {
      this.#shadowRoot?.getElementById(HIDE_NATIVE_ARROWS_STYLE_ID)?.remove();
    }

    #getMobileHeight() {
      const height = Number(this.getAttribute("data-embed-height-mobile"));

      return Number.isFinite(height) && height > 0 ? height : 0;
    }

    #applyLayoutStyle() {
      if (!this.#shadowRoot) return;

      let style = this.#shadowRoot.getElementById(LAYOUT_STYLE_ID);

      if (!style) {
        style = document.createElement("style");
        style.id = LAYOUT_STYLE_ID;
        this.#shadowRoot.append(style);
      }

      const mobileHeight = this.#getMobileHeight();

      style.textContent = mobileHeight
        ? `${LAYOUT_CSS}${mobileHeightCss(mobileHeight)}`
        : LAYOUT_CSS;
    }

    #removeLayoutStyle() {
      this.#shadowRoot?.getElementById(LAYOUT_STYLE_ID)?.remove();
    }

    #renderProgress() {
      if (!this.#progress) return;

      const segmentCount = this.#getViewCount();
      const active = Math.min(this.#index, segmentCount - 1);

      // Recreating the segments each time would cancel the width transition,
      // since a new element has nothing to animate from. Rebuild only when the
      // count changes, then move the active class.
      if (this.#progress.children.length !== segmentCount) {
        const segments = Array.from({ length: segmentCount }, () => {
          const segment = document.createElement("span");

          segment.className = "carousel-pagination__bullet";

          return segment;
        });

        this.#progress.replaceChildren(...segments);
      }

      for (const [index, segment] of [...this.#progress.children].entries()) {
        segment.classList.toggle(
          "carousel-pagination__bullet--active",
          index === active,
        );
      }
    }
  }

  customElements.define(COMPONENT_NAME, UgcVideowiseCarousel);
})();
